import type { EstadoTienda } from '../types/metas.types';

/**
 * Cómo se nombra y se pinta cada estado de una tienda en la semana.
 *
 *   sin_captura → borrador ─(gerente)→ revision ─(supervisor)→ enviada → autorizada
 *                   ↑   │                  │                      └─→ rechazada → enviada
 *                   │   │   devolver (con comentario) ─┘
 *                   │   └──────── enviar (el supervisor, directo) ──→ enviada
 *                   └──── reabrir (sólo admin) ─────────────────────────────┘
 *
 * Decisión del 29-sep: enviada y autorizada son de SÓLO LECTURA (la API
 * responde 409 si se intenta guardar). Para corregir algo enviado, el regional
 * lo rechaza con comentario; una autorizada sólo la reabre un administrador.
 *
 * 21-oct-2026 (decisión del usuario, fase B): el GERENTE captura desde la app
 * con la cuenta de la tienda y la manda «al supervisor» (`revision`). El
 * supervisor la ajusta (sigue en `revision`), se la devuelve con un comentario
 * (vuelve a borrador) o la envía al regional.
 */
export const ETIQUETA_ESTADO: Record<EstadoTienda, string> = {
  sin_captura: 'Sin captura',
  borrador: 'Borrador',
  revision: 'Con el supervisor',
  enviada: 'Enviada',
  autorizada: 'Autorizada',
  rechazada: 'Rechazada',
};

export type SeveridadTag = 'success' | 'info' | 'warning' | 'danger' | 'secondary';

export const SEVERIDAD_ESTADO: Record<EstadoTienda, SeveridadTag> = {
  sin_captura: 'secondary',
  borrador: 'info',
  revision: 'warning',
  enviada: 'warning',
  autorizada: 'success',
  rechazada: 'danger',
};

/**
 * Tono de la pastilla de cada estado (diseño: pastillas suaves).
 *   Sin captura y Borrador → neutro (gris) · Con el supervisor → aviso (ámbar)
 *   Enviada → info (azul) · Autorizada → éxito (verde) · Rechazada → error (rojo)
 * El color nunca va solo: la pastilla siempre lleva el nombre del estado.
 */
export type TonoEstado = 'neutro' | 'info' | 'exito' | 'error' | 'aviso';

export const TONO_ESTADO: Record<EstadoTienda, TonoEstado> = {
  sin_captura: 'neutro',
  borrador: 'neutro',
  revision: 'aviso',
  enviada: 'info',
  autorizada: 'exito',
  rechazada: 'error',
};

export const tonoEstado = (e: EstadoTienda | string): TonoEstado => TONO_ESTADO[e as EstadoTienda] ?? 'neutro';

/**
 * Estados en que el supervisor (o el admin) puede escribir (espejo de
 * `esEditable` de la API). `revision` desde el 21-oct-2026: ajusta lo que le
 * mandó el gerente sin sacarlo de su bandeja.
 */
export const esEditable = (e: EstadoTienda | string): boolean =>
  e === 'sin_captura' || e === 'borrador' || e === 'revision' || e === 'rechazada';

/** Enviada o autorizada: la captura se ve, no se toca. */
export const esSoloLecturaPorEstado = (e: EstadoTienda | string): boolean =>
  e === 'enviada' || e === 'autorizada';

/** Por qué la captura es de sólo lectura por su estado, en palabras de la API. */
export const MENSAJE_ENVIADA_SOLO_LECTURA =
  'La tienda está enviada: es de sólo lectura hasta que el regional la resuelva.';
export const MENSAJE_AUTORIZADA_SOLO_ADMIN = 'Autorizada: sólo un administrador puede reabrirla.';

export const etiquetaEstado = (e: EstadoTienda | string): string =>
  ETIQUETA_ESTADO[e as EstadoTienda] ?? e;

export const severidadEstado = (e: EstadoTienda | string): SeveridadTag =>
  SEVERIDAD_ESTADO[e as EstadoTienda] ?? 'secondary';

/**
 * El estado dicho para quien REVISA la captura (el supervisor en su pestaña
 * Captura, 21-oct-2026, diseño `Supervisor-revision`): lo que le mandó el
 * gerente es «Te la envió el gerente» y el borrador que le devolvió,
 * «Devuelta al gerente». Lo demás, como siempre.
 *
 * Sólo con la captura de la tienda encendida (`capturaTienda`). Por omisión
 * está apagada (22-oct-2026, decisión del usuario: «de momento seguir dejando
 * la tarea de capturar la meta a los supervisores») y cada estado se dice como
 * antes, sin el gerente.
 */
export function textoEstadoCaptura(e: EstadoTienda | string, devuelta = false, capturaTienda = false): string {
  if (!capturaTienda) return etiquetaEstado(e);
  if (e === 'revision') return 'Te la envió el gerente';
  if (e === 'borrador' && devuelta) return 'Devuelta al gerente';
  return etiquetaEstado(e);
}

/** Acciones de la bitácora (`metas_evento.accion`) en palabras. */
const ACCIONES: Record<string, string> = {
  guardar: 'Guardó la captura',
  // 21-oct-2026 (fase B): el gerente, con la cuenta de la tienda, y la devolución del supervisor.
  enviar_supervisor: 'La envió al supervisor (gerente)',
  devolver: 'La devolvió al gerente',
  enviar: 'Envió a autorización',
  autorizar: 'Autorizó',
  rechazar: 'Rechazó',
  reabrir: 'Reabrió (volvió a borrador)',
  // La tienda de PLANTILLA reclamó a su gente: la meta que otra tienda le puso
  // sólo porque vendió ahí se borra, y esa otra tienda vuelve a borrador. Sin
  // la etiqueta, el historial de la tienda afectada decía «meta.liberada».
  'meta.liberada': 'Meta liberada (la reclamó su tienda de plantilla)',
  'alcance.asignar': 'Asignó alcance',
  'alcance.revocar': 'Retiró alcance',
  // Fase 3: las condiciones quedan en la bitácora con la semana desde la que
  // valen, así que aparecen en el historial de esa semana (las de antes del
  // 21-oct-2026, cuando salieron de la captura).
  'condicion.proponer': 'Propuso condiciones de la tienda',
  'condicion.autorizar': 'Autorizó condiciones de la tienda',
  'condicion.rechazar': 'Rechazó condiciones de la tienda',
  'condicion.retirar': 'Retiró su propuesta de condiciones',
  'suplente.proponer': 'Propuso un suplente en desarrollo',
  'suplente.autorizar': 'Autorizó un suplente',
  'suplente.rechazar': 'Rechazó un suplente',
  'suplente.certificar': 'Registró la certificación de un suplente',
  'suplente.retirar': 'Retiró una propuesta de suplente',
};

export const etiquetaAccion = (a: string): string => ACCIONES[a] ?? a;
